import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Paper,
  Stack,
  Divider,
  Alert,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  ToggleButtonGroup,
  ToggleButton,
  Card,
  CardContent,
  Grid,
  Chip,
  Skeleton,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {useGetSubscriptionDetailsQuery} from '../../redux/thunks/sp/getsubscriptiondetails';
import {useSwitchSubscriptionMutation} from '../../redux/thunks/sp/switchsubscription';
import {useCancelSubscriptionMutation} from '../../redux/thunks/sp/cancelsubscription';
import {useReactivateSubscriptionMutation} from '../../redux/thunks/sp/reactivatesubscription';

import CreditCardIcon from '@mui/icons-material/CreditCard';
import EventIcon from '@mui/icons-material/Event';
import PaymentIcon from '@mui/icons-material/Payment';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import FlashOnIcon from '@mui/icons-material/FlashOn';

const SubscriptionManagement = ({ onClose }) => {
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [openReactivateDialog, setOpenReactivateDialog] = useState(false);
  const [openSwitchDialog, setOpenSwitchDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const user = useSelector((state) => state.auth.user);
  const userToken = useSelector((state) => state.auth.token);
  
  // Fetch subscription details
  const { 
    data: subscriptionDetails, 
    error: subscriptionError, 
    isLoading: subscriptionLoading,
    refetch: refetchSubscription
  } = useGetSubscriptionDetailsQuery({ 
    token: user._id, 
    authType: 'token' 
  });
  
  const [cancelSubscription, { isLoading: isCancelling }] = useCancelSubscriptionMutation();
  const [switchSubscriptionPlan, { isLoading: isSwitching }] = useSwitchSubscriptionMutation();
  const [reactivateSubscription, { isLoading: isReactivating }] = useReactivateSubscriptionMutation();
  
  // Current plan information
  const currentPlan = subscriptionDetails?.planType || user?.accountDetails?.planType || 'Monthly';
  const currentStatus = subscriptionDetails?.status || 'active';
  const willCancel = subscriptionDetails?.cancelAtPeriodEnd || false;
  const nextBillingDate = subscriptionDetails?.currentPeriodEnd ? new Date(subscriptionDetails.currentPeriodEnd) : null;
  const paymentMethod = subscriptionDetails?.paymentMethod || null;
  
  // Format date for display
  const formatDate = (date) => {
    if (!date) return 'Not available';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: isMobile ? 'short' : 'long',
      day: 'numeric'
    });
  };
  
  // For cancellation dialog
  const handleOpenCancelDialog = () => {
    setErrorMessage('');
    setSuccessMessage('');
    setOpenCancelDialog(true);
  };
  
  const handleCloseCancelDialog = () => {
    setOpenCancelDialog(false);
  };
  
  // For reactivation dialog
  const handleOpenReactivateDialog = () => {
    setErrorMessage('');
    setSuccessMessage('');
    setOpenReactivateDialog(true);
  };
  
  const handleCloseReactivateDialog = () => {
    setOpenReactivateDialog(false);
  };
  
  // For plan switching dialog
  const handleOpenSwitchDialog = () => {
    setErrorMessage('');
    setSuccessMessage('');
    setSelectedPlan(null);
    setOpenSwitchDialog(true);
  };
  
  const handleCloseSwitchDialog = () => {
    setOpenSwitchDialog(false);
  };
  
  const handleSwitchPlanChange = (event, newPlan) => {
    if (newPlan !== null) {
      setSelectedPlan(newPlan);
    }
  };
  
  // Handle subscription cancellation
  const handleCancelSubscription = async () => {
    try {
      setErrorMessage('');
      
      const payload = {
        auth: { token: userToken, authType: 'token' },
        userId: user._id,
        reason: cancelReason
      };
      
      const response = await cancelSubscription({ payload }).unwrap();
      
      setSuccessMessage(response.message || 'Your subscription has been cancelled successfully.');
      handleCloseCancelDialog();
      
      // Refetch subscription details to update the UI
      refetchSubscription();
      
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      setErrorMessage(error.data?.message || 'Failed to cancel subscription. Please try again.');
    }
  };
  
  // Handle subscription reactivation
  const handleReactivateSubscription = async () => {
    try {
      setErrorMessage('');
      
      const payload = {
        auth: { token: user._id, authType: 'token' },
        
      };
      
      const response = await reactivateSubscription({ payload }).unwrap();
      
      setSuccessMessage(response.message || 'Your subscription has been reactivated successfully.');
      handleCloseReactivateDialog();
      
      // Refetch subscription details to update the UI
      refetchSubscription();
      
    } catch (error) {
      console.error('Error reactivating subscription:', error);
      setErrorMessage(error.data?.message || 'Failed to reactivate subscription. Please try again.');
    }
  };
  
  // Handle subscription plan switch
  const handleSwitchPlan = async () => {
    if (!selectedPlan || selectedPlan === currentPlan) {
      setErrorMessage('Please select a different plan.');
      return;
    }
    
    try {
      setErrorMessage('');
      
      const payload = {
        auth: { token: userToken, authType: 'token' },
        userId: user._id,
        newPlan: selectedPlan
      };
      
      const response = await switchSubscriptionPlan({ payload }).unwrap();
      
      setSuccessMessage(response.message || `Your subscription has been switched to the ${selectedPlan} plan.`);
      handleCloseSwitchDialog();
      
      // Refetch subscription details to update the UI
      refetchSubscription();
      
    } catch (error) {
      console.error('Error switching plan:', error);
      setErrorMessage(error.data?.message || 'Failed to switch plan. Please try again.');
    }
  };
  
  // Only show this component for premium users
  if (user?.accountDetails?.type !== 'premium') {
    return (
      <Box p={{ xs: 2, sm: 3 }}>
        <Typography variant="body1" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
          You don't currently have a premium subscription.
        </Typography>
      </Box>
    );
  }
  
  // Show loading state
  if (subscriptionLoading) {
    return (
      <Box p={{ xs: 2, sm: 3 }}>
        <Skeleton variant="rectangular" width="100%" height={isMobile ? 150 : 200} />
        <Skeleton variant="text" width="60%" height={30} sx={{ mt: 2 }} />
        <Skeleton variant="text" width="40%" height={30} sx={{ mt: 1 }} />
        <Box mt={3} display="flex" gap={2} flexDirection={isMobile ? 'column' : 'row'}>
          <Skeleton variant="rectangular" width={isMobile ? '100%' : 120} height={40} />
          <Skeleton variant="rectangular" width={isMobile ? '100%' : 120} height={40} />
        </Box>
      </Box>
    );
  }
  
  return (
    <Box p={{ xs: 1, sm: 2 }}>
      <Typography 
        variant="h5" 
        gutterBottom
        sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
        Your Subscription
      </Typography>
      
      <Divider sx={{ my: 2 }} />

      {/* Inaugural Pricing Notice - Mobile optimized */}
      <Alert 
        severity="info" 
        icon={<FlashOnIcon />}
        sx={{ 
          mb: { xs: 2, sm: 3 }, 
          bgcolor: 'primary.50', 
          border: '1px solid',
          borderColor: 'primary.200',
          borderRadius: { xs: 1.5, sm: 2 }
        }}>
        <Typography 
          variant="body2" 
          sx={{ 
            fontWeight: 500,
            fontSize: { xs: '0.8rem', sm: '0.875rem' },
            lineHeight: 1.4
          }}>
          <strong>You're enjoying Inaugural Launch Pricing:</strong><br />
          Monthly $20 (reg. $50) • Annual $199 (reg. $500)<br />
          <Box component="em" sx={{ color: 'primary.main' }}>
            Thank you for being a founding member!
          </Box>
        </Typography>
      </Alert>
      
      {/* Subscription Details Card - Mobile optimized */}
      <Card variant="outlined" sx={{ mb: { xs: 2, sm: 3 } }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            <Grid item xs={12}>
              <Box 
                display="flex" 
                justifyContent="space-between" 
                alignItems="center"
                flexWrap="wrap"
                gap={1}>
                <Typography 
                  variant="h6"
                  sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                  {currentPlan} Plan
                </Typography>
                <Chip 
                  label={willCancel ? "Cancelling Soon" : currentStatus === 'active' ? "Active" : currentStatus} 
                  color={willCancel ? "warning" : currentStatus === 'active' ? "success" : "default"}
                  size={isMobile ? "small" : "medium"}
                  sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}
                />
              </Box>
            </Grid>
            
            {willCancel && (
              <Grid item xs={12}>
                <Alert severity="warning" icon={<WarningIcon />}>
                  <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                    Your subscription is scheduled to cancel on {formatDate(nextBillingDate)}. You'll still have access until then.
                  </Typography>
                </Alert>
              </Grid>
            )}
            
            <Grid item xs={12} sm={6}>
              <Box display="flex" alignItems="center" gap={1}>
                <EventIcon color="primary" fontSize={isMobile ? "small" : "medium"} />
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                  Next billing:
                </Typography>
              </Box>
              <Typography 
                variant="body1" 
                ml={{ xs: 3, sm: 4 }}
                sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                {formatDate(nextBillingDate)}
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Box display="flex" alignItems="center" gap={1}>
                <PaymentIcon color="primary" fontSize={isMobile ? "small" : "medium"} />
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                  Billing cycle:
                </Typography>
              </Box>
              <Typography 
                variant="body1" 
                ml={{ xs: 3, sm: 4 }}
                sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                {currentPlan === 'Monthly' ? 'Monthly ($20)' : 'Yearly ($199)'}
              </Typography>
            </Grid>
            
            {paymentMethod && (
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={1}>
                  <CreditCardIcon color="primary" fontSize={isMobile ? "small" : "medium"} />
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                    Payment method:
                  </Typography>
                </Box>
                <Typography 
                  variant="body1" 
                  ml={{ xs: 3, sm: 4 }}
                  sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                  {paymentMethod.brand.charAt(0).toUpperCase() + paymentMethod.brand.slice(1)} •••• {paymentMethod.last4}
                  <Typography 
                    component="span" 
                    variant="body2" 
                    color="text.secondary" 
                    ml={1}
                    sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                    (expires {paymentMethod.expMonth}/{paymentMethod.expYear})
                  </Typography>
                </Typography>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>
      
      {successMessage && (
        <Alert 
          severity="success" 
          sx={{ 
            my: 2,
            fontSize: { xs: '0.8rem', sm: '0.875rem' }
          }} 
          icon={<CheckCircleIcon />}>
          {successMessage}
        </Alert>
      )}
      
      {errorMessage && (
        <Alert 
          severity="error" 
          sx={{ 
            my: 2,
            fontSize: { xs: '0.8rem', sm: '0.875rem' }
          }}>
          {errorMessage}
        </Alert>
      )}
      
      {subscriptionError && (
        <Alert 
          severity="error" 
          sx={{ 
            my: 2,
            fontSize: { xs: '0.8rem', sm: '0.875rem' }
          }}>
          {subscriptionError.data?.message || "Failed to load subscription details"}
        </Alert>
      )}
      
      {/* Action Buttons - Mobile stacked */}
      <Stack 
        direction={isMobile ? 'column' : 'row'} 
        spacing={2} 
        mt={3}>
        {!willCancel ? (
          <>
            <Button 
              variant="outlined" 
              color="primary"
              onClick={handleOpenSwitchDialog}
              disabled={isSwitching || isCancelling || willCancel || isReactivating}
              fullWidth={isMobile}
              sx={{ 
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                py: { xs: 1.5, sm: 1 }
              }}
            >
              {currentPlan === 'Monthly' 
                ? (isMobile ? 'Upgrade to Yearly (Save $41)' : 'Upgrade to Yearly (Save $41 with Inaugural Pricing)')
                : 'Switch to Monthly'
              }
            </Button>
            
            <Button 
              variant="outlined" 
              color="error"
              onClick={handleOpenCancelDialog}
              disabled={isSwitching || isCancelling || willCancel || isReactivating}
              fullWidth={isMobile}
              sx={{ 
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                py: { xs: 1.5, sm: 1 }
              }}
            >
              Cancel Subscription
            </Button>
          </>
        ) : (
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleOpenReactivateDialog}
            disabled={isReactivating}
            fullWidth
            startIcon={<AutorenewIcon />}
            sx={{ 
              fontSize: { xs: '0.9rem', sm: '1rem' },
              py: { xs: 1.5, sm: 1 }
            }}
          >
            {isReactivating ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Keep My Subscription"
            )}
          </Button>
        )}
      </Stack>
      
      {/* Switch Plan Dialog - Mobile optimized */}
      <Dialog 
        open={openSwitchDialog} 
        onClose={handleCloseSwitchDialog}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth>
        <DialogTitle sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
          Switch Subscription Plan
        </DialogTitle>
        <DialogContent>
          <DialogContentText 
            mb={3}
            sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
            Select the plan you want to switch to. Billing will be adjusted accordingly.
          </DialogContentText>
          
          <Box mb={3}>
            <Typography 
              variant="subtitle2" 
              gutterBottom
              sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
              Current Plan: <strong>{currentPlan}</strong>
            </Typography>
          </Box>
          
          <FormControl component="fieldset">
            <ToggleButtonGroup
              value={selectedPlan}
              exclusive
              onChange={handleSwitchPlanChange}
              aria-label="subscription plan"
              orientation={isMobile ? "vertical" : "horizontal"}
              sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: 1,
                width: '100%'
              }}
            >
              <ToggleButton 
                value="Monthly" 
                aria-label="Monthly plan"
                disabled={currentPlan === 'Monthly'}
                sx={{ 
                  justifyContent: 'flex-start', 
                  py: { xs: 1.5, sm: 1.5 },
                  px: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  width: '100%',
                  '&.Mui-selected': {
                    borderColor: 'primary.main',
                    bgcolor: 'primary.light',
                    opacity: 0.1
                  }
                }}
              >
                <Box textAlign="left" width="100%">
                  <Typography 
                    variant="subtitle1"
                    sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                    Monthly Plan
                  </Typography>
                  <Typography 
                    variant="body2"
                    sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                    $20 billed monthly (Inaugural: was $50)
                  </Typography>
                </Box>
              </ToggleButton>
              
              <ToggleButton 
                value="Yearly" 
                aria-label="Yearly plan"
                disabled={currentPlan === 'Yearly'}
                sx={{ 
                  justifyContent: 'flex-start', 
                  py: { xs: 1.5, sm: 1.5 },
                  px: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  width: '100%',
                  '&.Mui-selected': {
                    borderColor: 'primary.main',
                    bgcolor: 'primary.light',
                    opacity: 0.1
                  }
                }}
              >
                <Box textAlign="left" width="100%">
                  <Typography 
                    variant="subtitle1"
                    sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                    Yearly Plan
                  </Typography>
                  <Typography 
                    variant="body2"
                    sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                    $199 billed annually (Inaugural: was $500)
                  </Typography>
                </Box>
              </ToggleButton>
            </ToggleButtonGroup>
          </FormControl>
          
          {errorMessage && (
            <Alert 
              severity="error" 
              sx={{ 
                mt: 2,
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}>
              {errorMessage}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
          <Button 
            onClick={handleCloseSwitchDialog} 
            disabled={isSwitching}
            sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
            Cancel
          </Button>
          <Button 
            onClick={handleSwitchPlan} 
            variant="contained" 
            color="primary" 
            disabled={!selectedPlan || isSwitching || currentPlan === selectedPlan}
            sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
          >
            {isSwitching ? <CircularProgress size={20} /> : 'Switch Plan'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Cancel Subscription Dialog - Mobile optimized */}
      <Dialog 
        open={openCancelDialog} 
        onClose={handleCloseCancelDialog}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth>
        <DialogTitle sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
          Cancel Your Subscription
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
            Are you sure you want to cancel your premium subscription? Your benefits will continue until the end of your current billing period, then your account will revert to the Free tier.
          </DialogContentText>
          
          <Box mt={2}>
            <FormControl component="fieldset" fullWidth>
              <Typography 
                variant="subtitle2" 
                gutterBottom
                sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                Please tell us why you're cancelling (optional):
              </Typography>
              
              <RadioGroup
                aria-label="cancel reason"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
              >
                <FormControlLabel 
                  value="Too expensive" 
                  control={<Radio size={isMobile ? "small" : "medium"} />} 
                  label={
                    <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                      Too expensive
                    </Typography>
                  } 
                />
                <FormControlLabel 
                  value="Not using enough" 
                  control={<Radio size={isMobile ? "small" : "medium"} />} 
                  label={
                    <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                      Not using enough
                    </Typography>
                  } 
                />
                <FormControlLabel 
                  value="Missing features" 
                  control={<Radio size={isMobile ? "small" : "medium"} />} 
                  label={
                    <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                      Missing features
                    </Typography>
                  } 
                />
                <FormControlLabel 
                  value="Switching services" 
                  control={<Radio size={isMobile ? "small" : "medium"} />} 
                  label={
                    <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                      Switching to another service
                    </Typography>
                  } 
                />
                <FormControlLabel 
                  value="Other" 
                  control={<Radio size={isMobile ? "small" : "medium"} />} 
                  label={
                    <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                      Other reason
                    </Typography>
                  } 
                />
              </RadioGroup>
            </FormControl>
          </Box>
          
          {errorMessage && (
            <Alert 
              severity="error" 
              sx={{ 
                mt: 2,
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}>
              {errorMessage}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
          <Button 
            onClick={handleCloseCancelDialog} 
            disabled={isCancelling}
            sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
            Keep Subscription
          </Button>
          <Button 
            onClick={handleCancelSubscription} 
            variant="contained" 
            color="error" 
            disabled={isCancelling}
            sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
          >
            {isCancelling ? <CircularProgress size={20} /> : 'Confirm Cancellation'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Reactivate Subscription Dialog - Mobile optimized */}
      <Dialog 
        open={openReactivateDialog} 
        onClose={handleCloseReactivateDialog}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth>
        <DialogTitle sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
          Keep Your Subscription
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
            Are you sure you want to keep your subscription? This will cancel the scheduled cancellation and your subscription will continue to renew automatically.
          </DialogContentText>
          
          {errorMessage && (
            <Alert 
              severity="error" 
              sx={{ 
                mt: 2,
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}>
              {errorMessage}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
          <Button 
            onClick={handleCloseReactivateDialog} 
            disabled={isReactivating}
            sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
            No, Cancel Later
          </Button>
          <Button 
            onClick={handleReactivateSubscription} 
            variant="contained" 
            color="primary" 
            disabled={isReactivating}
            sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
          >
            {isReactivating ? <CircularProgress size={20} /> : 'Yes, Keep My Subscription'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubscriptionManagement;